import { Button, User } from "components/elements";
import { TextEditor } from "components/form/elements";
import {
  Column,
  Row,
  Section,
  Subsection,
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from "components/layout";
import { RestrictedArea, ROLES } from "containers/Permissions";
import {
  APP_CATEGORIES,
  COMMERCIAL_AGREEMENT_TYPE,
  CONTRACT_ORIGIN,
  CONTRACT_STATUS,
  SUPPORT_PRIORITY,
  YES_NO_UNKNOWN,
} from "hooks/tivoApps";
import { NO_VALUE_PLACEHOLDER } from "src/constants";
import { pathToUrl } from "utils/url";

import {
  ACR_RIGHTS_LABELS,
  CSP_BIZ_MODEL_LABELS,
  CSP_GROUP_LABELS,
  PATHS,
} from "../../constants";
import {
  Countries,
  ImagesSection,
  TechnicalText,
  PresentationsSection,
} from "../components";
import { TEXTS } from "../constants";
import styles from "../TiVoAppDetailsTabs.scss";
import { DetailsTabProps } from "../types";
import { formatGroup } from "../utils";

export function TiVoAppDetailsProviderTab({ provider }: DetailsTabProps) {
  const renderAllowListSection = () => {
    if (!provider.allowList || provider.allowList.length === 0) {
      return null;
    }
    return (
      <Section
        header={TEXTS.PROVIDER.ALLOW_LIST_SECTION_HEADER}
        tooltip={TEXTS.PROVIDER.ALLOW_LIST_TOOLTIP}
      >
        <Table>
          <TableHeader>
            <TableHeaderColumn>
              {TEXTS.PROVIDER.REG_EXP_COL_HEADER}
            </TableHeaderColumn>
          </TableHeader>
          <TableBody>
            {provider.allowList.map((regExp) => (
              <TableRow key={regExp}>
                <TableRowColumn>
                  <TechnicalText>{regExp}</TechnicalText>
                </TableRowColumn>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    );
  };

  return (
    <>
      <RestrictedArea
        allowed={[ROLES.tivo.provider, ROLES.tivo.bizDev]}
        showFallback={false}
      >
        <Button
          className={styles.editButton}
          dataTestId="edit-provider-button"
          to={pathToUrl(PATHS.editProvider, {
            id: provider.id,
            tabId: "",
          })}
        >
          {TEXTS.PROVIDER.PROVIDER_EDIT_BUTTON}
        </Button>
      </RestrictedArea>
      <Section header={TEXTS.PROVIDER.BASIC_DETAILS_SECTION_HEADER}>
        <Row>
          <Column>
            <Subsection
              header={TEXTS.PROVIDER.APP_LABEL_SUBS_HEADER}
              tooltip={TEXTS.PROVIDER.APP_LABEL_TOOLTIP}
            >
              {provider.label || NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection
              header={TEXTS.PROVIDER.DISPLAY_NAME}
              tooltip={TEXTS.PROVIDER.DISPLAY_NAME_TOOLTIP}
            >
              {provider.displayName}
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.APP_CATEGORY_SUBS_HEADER}>
              {formatGroup(provider.appCategories, APP_CATEGORIES) ||
                NO_VALUE_PLACEHOLDER}
            </Subsection>
          </Column>
          <Column>
            <Subsection
              header={TEXTS.PROVIDER.PROVIDER_ID_SUBS_HEADER}
              tooltip={TEXTS.PROVIDER.PROVIDER_ID_TOOLTIP}
            >
              {provider.providerId || NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection
              header={TEXTS.PROVIDER.UNIVERSAL_PROVIDER_ID_SUBS_HEADER}
              tooltip={TEXTS.PROVIDER.UNIVERSAL_PROVIDER_ID_TOOLTIP}
            >
              {provider.universalProviderId || NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection
              header={TEXTS.PROVIDER.JW_PROVIDER_ID_SUBS_HEADER}
              tooltip={TEXTS.PROVIDER.JW_PROVIDER_ID_TOOLTIP}
            >
              {provider.jwProviderId && provider.jwProviderId.length > 0
                ? provider.jwProviderId.join(", ")
                : NO_VALUE_PLACEHOLDER}
            </Subsection>
          </Column>
        </Row>
      </Section>
      {renderAllowListSection()}
      <ImagesSection assets={provider.assets} />
      <Section header={TEXTS.PROVIDER.CSP_SUPPORT_SECTION_HEADER}>
        <Row>
          <Column>
            <Subsection header={TEXTS.PROVIDER.SUPPORT_PRIORITY_SUBS_HEADER}>
              {SUPPORT_PRIORITY[provider.supportPriority!] ||
                NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.SUPPORT_EMAIL_SUBS_HEADER}>
              {provider.supportEmail || NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.SUPPORT_PHONE_SUBS_HEADER}>
              {provider.supportPhone || NO_VALUE_PLACEHOLDER}
            </Subsection>
          </Column>
          <Column>
            <Subsection header={TEXTS.PROVIDER.SUPPORT_NOTES_SUBS_HEADER}>
              <pre>{provider.supportNotes || NO_VALUE_PLACEHOLDER}</pre>
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.SUPPORT_SLA_SUBS_HEADER}>
              <pre>{provider.supportSla || NO_VALUE_PLACEHOLDER}</pre>
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.SUPPORT_CONTACTS_SUBS_HEADER}>
              <pre>
                {provider.supportAdditionalContacts || NO_VALUE_PLACEHOLDER}
              </pre>
            </Subsection>
          </Column>
        </Row>
      </Section>
      <Section header={TEXTS.PROVIDER.ASSIGNEES_HEADER}>
        <Row>
          <Column>
            <Subsection header={TEXTS.PROVIDER.ASSIGNEES_TAM}>
              <User
                userId={provider.assignedTam}
                requiredRoles={[ROLES.tivo.moderator]}
              />
            </Subsection>
          </Column>
          <Column>
            <Subsection header={TEXTS.PROVIDER.ASSIGNEES_BDM}>
              <User
                userId={provider.assignedBdm}
                requiredRoles={[ROLES.tivo.moderator]}
              />
            </Subsection>
          </Column>
        </Row>
      </Section>
      <Row>
        <Column>
          <Section header={TEXTS.PROVIDER.AGREEMENTS_SECTION_HEADER}>
            <Subsection header={TEXTS.PROVIDER.CONTRACT_STATUS_SUBS_HEADER}>
              {CONTRACT_STATUS[provider.contractStatus!] ||
                NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection
              header={TEXTS.PROVIDER.REQUIRES_CONFIRMATION_SUBS_HEADER}
            >
              {YES_NO_UNKNOWN[provider.cspConfirmationRequired || "unknown"]}
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.CSP_GROUP_SUSB_HEADER}>
              {CSP_GROUP_LABELS[provider.cspGroup!] || NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.CSP_BIZ_MODEL_SUBS_HEADER}>
              {formatGroup(provider.cspBusinessModel, CSP_BIZ_MODEL_LABELS)}
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.AGREEMENT_MODEL_SUBS_HEADER}>
              {formatGroup(
                provider.commercialAgreementType,
                COMMERCIAL_AGREEMENT_TYPE
              )}
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.ACR_RIGHTS_SUBS_HEADER}>
              {ACR_RIGHTS_LABELS[provider.acrRights!] || NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.CONTRACT_ORIGIN_SUBS_HEADER}>
              {CONTRACT_ORIGIN[provider.contractOrigin!] ||
                NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.AGREEMENT_SUMMARY_SUBS_HEADER}>
              <TextEditor
                initialValue={
                  provider.commercialAgreementSummary || NO_VALUE_PLACEHOLDER
                }
                readOnly
              />
            </Subsection>
          </Section>
          <Section header={TEXTS.PROVIDER.DEAL_AND_OBLIGATIONS_HEADER}>
            <Subsection
              header={TEXTS.PROVIDER.REPORTING_OBLIGATIONS_SUSB_HEADER}
            >
              <TextEditor
                initialValue={
                  provider.reportingObligations || NO_VALUE_PLACEHOLDER
                }
                readOnly
              />
            </Subsection>
            <Subsection header={TEXTS.PROVIDER.PROMO_OBLIGATIONS_SUBS_HEADER}>
              <TextEditor
                initialValue={
                  provider.promotionsObligations || NO_VALUE_PLACEHOLDER
                }
                readOnly
              />
            </Subsection>
            <Subsection
              header={TEXTS.PROVIDER.PLACEMENT_OBLIGATIONS_SUBS_HEADER}
            >
              <TextEditor
                initialValue={
                  provider.placementObligations || NO_VALUE_PLACEHOLDER
                }
                readOnly
              />
            </Subsection>
          </Section>
        </Column>
        <Column>
          <Section header={TEXTS.PROVIDER.COUNTRIES_SECTION_HEADER}>
            <Countries countries={provider.countries || []} />
          </Section>
        </Column>
      </Row>
      <Section header={TEXTS.PROVIDER.DEAL_SUMMARY_SUBS_HEADER}>
        <TextEditor
          initialValue={provider.dealSummary || NO_VALUE_PLACEHOLDER}
          readOnly
        />
      </Section>
      <Section header={TEXTS.PRESENTATION.NAME}>
        <PresentationsSection provider={provider} />
      </Section>
    </>
  );
}
